// StatsUtils.js
import { alpha } from '@mui/material';
import { format, isValid, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { safeFormatDate } from './TradeForm';
import { COLORS } from './StatsStyles';

// Helper function to calculate R value for a trade
export const getTradeR = (trade) => {
  if (trade.outcome === 'Stop Loss') return -1;
  
  if (trade.outcome === 'Take Profit') {
    if (Array.isArray(trade.tpMilestones)) {
      const exitMilestone = trade.tpMilestones.find(m => m.status === 'יציאה' && m.tp);
      if (exitMilestone) {
        const val = String(exitMilestone.tp).replace(/\s/g, '');
        const parts = val.split(':');
        if (parts.length === 2 && !isNaN(parts[1])) {
          return parseFloat(parts[1]);
        }
        if (val.endsWith('R')) {
          const rValue = parseFloat(val.replace('R', ''));
          if (!isNaN(rValue)) return rValue;
        }
        const directValue = parseFloat(val);
        if (!isNaN(directValue)) return directValue;
      }
    }
    if (trade.tpMilestoneExit) {
      const val = String(trade.tpMilestoneExit).replace(/\s/g, '');
      const parts = val.split(':');
      if (parts.length === 2 && !isNaN(parts[1])) {
        return parseFloat(parts[1]);
      }
      if (val.endsWith('R')) {
        const rValue = parseFloat(val.replace('R', ''));
        if (!isNaN(rValue)) return rValue;
      }
      const directValue = parseFloat(val);
      if (!isNaN(directValue)) return directValue;
    }
    return 1;
  }
  
  return 0;
};

// Filter trades based on various criteria
export const filterTrades = (trades, timeFilter, symbolFilter, userTriggerFilter) => {
  let filtered = [...trades];
  
  // Apply time filter
  if (timeFilter === 'last30') {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= thirtyDaysAgo);
  } else if (timeFilter === 'last90') {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= ninetyDaysAgo);
  } else if (timeFilter === 'thisYear') {
    const startOfYear = new Date(new Date().getFullYear(), 0, 1);
    filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= startOfYear);
  }
  
  // Apply symbol filter
  if (symbolFilter !== 'all') {
    filtered = filtered.filter(trade => trade.symbol === symbolFilter);
  }
  
  // Apply trigger filter
  if (userTriggerFilter !== 'all') {
    filtered = filtered.filter(trade => trade.matchedTrigger === userTriggerFilter);
  }
  
  return filtered;
};

// Calculate primary trade statistics
export const calculateStats = (realTrades) => {
  if (realTrades.length === 0) {
    return {
      totalR: 0,
      avgR: 0,
      winRate: 0,
      totalTrades: 0,
      winCount: 0,
      lossCount: 0,
      bestR: 0,
      worstR: 0,
      profitFactor: 0,
      expectancy: 0,
      bestDay: null,
      worstDay: null,
      averageDuration: 0,
      rValues: [],
      dailyR: []
    };
  }

  // Calculate basic stats
  const totalR = realTrades.reduce((acc, trade) => acc + getTradeR(trade), 0);
  const winCount = realTrades.filter(t => t.outcome === 'Take Profit').length;
  const lossCount = realTrades.filter(t => t.outcome === 'Stop Loss').length;
  const winRate = (winCount / realTrades.length) * 100;
  
  // Calculate R values for each trade
  const rValues = realTrades.map(trade => ({
    ...trade,
    r: getTradeR(trade)
  }));
  
  // Get best and worst R
  const bestR = Math.max(...rValues.map(t => t.r));
  const worstR = Math.min(...rValues.map(t => t.r));
  
  // Calculate profit factor
  const grossProfit = rValues.filter(t => t.r > 0).reduce((acc, t) => acc + t.r, 0);
  const grossLoss = Math.abs(rValues.filter(t => t.r < 0).reduce((acc, t) => acc + t.r, 0));
  const profitFactor = grossLoss === 0 ? grossProfit : grossProfit / grossLoss;
  
  // Calculate expectancy
  const expectancy = (winRate / 100) * (grossProfit / winCount || 0) - ((100 - winRate) / 100) * (grossLoss / lossCount || 0);
  
  // Group trades by date
  const tradesByDate = {};
  rValues.forEach(trade => {
    if (!tradesByDate[trade.date]) {
      tradesByDate[trade.date] = [];
    }
    tradesByDate[trade.date].push(trade);
  });
  
  // Calculate daily R
  const dailyR = Object.keys(tradesByDate).map(date => ({
    date,
    r: tradesByDate[date].reduce((acc, trade) => acc + trade.r, 0)
  }));
  
  // Get best and worst day
  const bestDay = dailyR.reduce((best, current) => !best || current.r > best.r ? current : best, null);
  const worstDay = dailyR.reduce((worst, current) => !worst || current.r < worst.r ? current : worst, null);
  
  // Calculate average duration
  const totalDuration = realTrades.reduce((acc, trade) => acc + (parseInt(trade.duration) || 0), 0);
  const averageDuration = totalDuration / realTrades.length;
  
  return {
    totalR,
    avgR: totalR / realTrades.length,
    winRate,
    totalTrades: realTrades.length,
    winCount,
    lossCount,
    bestR,
    worstR,
    profitFactor,
    expectancy,
    bestDay,
    worstDay,
    averageDuration,
    rValues,
    dailyR
  };
};

// Calculate user-specific statistics (for admin mode)
export const calculateUserStats = (isAdmin, realTrades, allUsers) => {
  if (!isAdmin || realTrades.length === 0) return [];

  // Group trades by user
  const tradesByUser = {};
  
  realTrades.forEach(trade => {
    const userId = trade.uid;
    if (!tradesByUser[userId]) {
      tradesByUser[userId] = [];
    }
    tradesByUser[userId].push(trade);
  });

  // Calculate stats for each user
  return Object.keys(tradesByUser).map(userId => {
    const userTrades = tradesByUser[userId];
    const userName = userTrades[0]?.userDisplayName || 'לא ידוע';
    const email = userTrades[0]?.email || allUsers.find(u => u.id === userId)?.email || 'לא ידוע';
    const trigger = allUsers.find(u => u.id === userId)?.trigger || 'לא ידוע';
    const tradingHours = allUsers.find(u => u.id === userId)?.tradingHours || 'לא ידוע';
    
    // Calculate basic stats
    const totalR = userTrades.reduce((acc, trade) => acc + getTradeR(trade), 0);
    const winCount = userTrades.filter(t => t.outcome === 'Take Profit').length;
    const lossCount = userTrades.filter(t => t.outcome === 'Stop Loss').length;
    const winRate = (winCount / userTrades.length) * 100;
    
    return {
      userId,
      userName,
      email,
      trigger,
      tradingHours,
      totalTrades: userTrades.length,
      totalR,
      avgR: totalR / userTrades.length,
      winRate,
      winCount,
      lossCount
    };
  }).sort((a, b) => b.avgR - a.avgR); // Sort by average R
};

// Prepare data for all charts
export const prepareChartsData = (realTrades, theme) => {
  // If no trades, return empty data
  if (realTrades.length === 0) {
    return {
      cumulativeR: [], 
      rByDay: [],
      winRateByDay: [],
      rBySymbol: [],
      rBySession: [],
      rByTrigger: [],
      rByReport: [],
      rByTimeDistribution: [],
      durationVsR: [],
      monthlyPerformance: [],
      successRateByTimeframe: [],
      tpDistribution: [],
      stopReasons: [],
      marketPhasePerformance: [],
      rByUser: []
    };
  }

  // Prepare cumulative R chart data
  const computedCumulativeR = [];
  let runningTotal = 0;

  for (let i = 0; i < realTrades.length; i++) {
    const trade = realTrades[i];
    const r = getTradeR(trade);
    runningTotal += r;

    computedCumulativeR.push({
      tradeNumber: i + 1,
      date: trade.date,
      r: r,
      cumulative: runningTotal,
      symbol: trade.symbol,
      session: trade.session,
      outcome: trade.outcome,
      userId: trade.uid,
      userName: trade.userDisplayName || trade.userName
    });
  }

  // Prepare R by day of week chart data
  const rByDayMap = {};
  const countByDayMap = {};
  const winsByDayMap = {};
  
  realTrades.forEach(trade => {
    const dayOfWeek = trade.dayOfWeek;
    const r = getTradeR(trade);
    
    if (!rByDayMap[dayOfWeek]) {
      rByDayMap[dayOfWeek] = 0;
      countByDayMap[dayOfWeek] = 0;
      winsByDayMap[dayOfWeek] = 0;
    }
    
    rByDayMap[dayOfWeek] += r;
    countByDayMap[dayOfWeek]++;
    
    if (trade.outcome === 'Take Profit') {
      winsByDayMap[dayOfWeek]++;
    }
  });
  
  const daysOrder = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  
  const rByDay = daysOrder.map(day => ({
    day,
    r: rByDayMap[day] || 0,
    count: countByDayMap[day] || 0,
    avgR: countByDayMap[day] ? (rByDayMap[day] / countByDayMap[day]) : 0
  }));
  
  const winRateByDay = daysOrder.map(day => ({
    day,
    winRate: countByDayMap[day] ? (winsByDayMap[day] / countByDayMap[day]) * 100 : 0,
    totalTrades: countByDayMap[day] || 0
  }));

  // Prepare R by symbol chart data
  const rBySymbolMap = {};
  const countBySymbolMap = {};
  
  realTrades.forEach(trade => {
    const symbol = trade.symbol;
    const r = getTradeR(trade);
    
    if (!rBySymbolMap[symbol]) {
      rBySymbolMap[symbol] = 0;
      countBySymbolMap[symbol] = 0;
    }
    
    rBySymbolMap[symbol] += r;
    countBySymbolMap[symbol]++;
  });
  
  const rBySymbol = Object.keys(rBySymbolMap).map(symbol => ({
    symbol,
    r: rBySymbolMap[symbol],
    count: countBySymbolMap[symbol],
    avgR: rBySymbolMap[symbol] / countBySymbolMap[symbol]
  }));

  // Prepare R by session chart data
  const rBySessionMap = {};
  const countBySessionMap = {};
  
  realTrades.forEach(trade => {
    const session = trade.session;
    const r = getTradeR(trade);
    
    if (!rBySessionMap[session]) {
      rBySessionMap[session] = 0;
      countBySessionMap[session] = 0;
    }
    
    rBySessionMap[session] += r;
    countBySessionMap[session]++;
  });
  
  const rBySession = Object.keys(rBySessionMap).map(session => ({
    session,
    r: rBySessionMap[session],
    count: countBySessionMap[session],
    avgR: rBySessionMap[session] / countBySessionMap[session]
  }));

  // Prepare R by trigger chart data
  const rByTriggerMap = {};
  const countByTriggerMap = {};
  
  realTrades.forEach(trade => {
    const trigger = trade.matchedTrigger;
    const r = getTradeR(trade);
    
    if (!rByTriggerMap[trigger]) {
      rByTriggerMap[trigger] = 0;
      countByTriggerMap[trigger] = 0;
    }
    
    rByTriggerMap[trigger] += r;
    countByTriggerMap[trigger]++;
  });
  
  const rByTrigger = Object.keys(rByTriggerMap).map(trigger => ({
    trigger,
    r: rByTriggerMap[trigger],
    count: countByTriggerMap[trigger],
    avgR: rByTriggerMap[trigger] / countByTriggerMap[trigger]
  }));

  // Prepare R by report chart data
  const rByReportMap = {};
  const countByReportMap = {};
  
  realTrades.forEach(trade => {
    const report = trade.report || 'לא הוגדר';
    const r = getTradeR(trade);
    
    if (!rByReportMap[report]) {
      rByReportMap[report] = 0;
      countByReportMap[report] = 0;
    }
    
    rByReportMap[report] += r;
    countByReportMap[report]++;
  });
  
  const rByReport = Object.keys(rByReportMap)
    .filter(report => countByReportMap[report] >= 3) // Only include reports with at least 3 trades
    .map(report => ({
      report,
      r: rByReportMap[report],
      count: countByReportMap[report],
      avgR: rByReportMap[report] / countByReportMap[report]
    }))
    .sort((a, b) => b.avgR - a.avgR); // Sort by average R

  // Prepare R by user data (for admin mode)
  const rByUserMap = {};
  const countByUserMap = {};
  const winsByUserMap = {};
  
  realTrades.forEach(trade => {
    const userId = trade.uid;
    const userName = trade.userDisplayName || trade.userName;
    const userKey = `${userName} (${userId})`;
    const r = getTradeR(trade);
    
    if (!rByUserMap[userKey]) {
      rByUserMap[userKey] = 0;
      countByUserMap[userKey] = 0;
      winsByUserMap[userKey] = 0;
    }
    
    rByUserMap[userKey] += r;
    countByUserMap[userKey]++;
    
    if (trade.outcome === 'Take Profit') {
      winsByUserMap[userKey]++;
    }
  });
  
  const rByUser = Object.keys(rByUserMap).map(userKey => ({
    user: userKey.split(' (')[0], // Just the name part
    userId: userKey.split('(')[1].replace(')', ''), // Just the ID part
    r: rByUserMap[userKey],
    count: countByUserMap[userKey],
    avgR: rByUserMap[userKey] / countByUserMap[userKey],
    winRate: (winsByUserMap[userKey] / countByUserMap[userKey]) * 100
  })).sort((a, b) => b.avgR - a.avgR);

  // Prepare R by time distribution chart data
  const rByTimeMap = {};
  const countByTimeMap = {};
  
  realTrades.forEach(trade => {
    if (!trade.time) return;
    
    // Group by hour
    const hour = trade.time.split(':')[0];
    const timeKey = `${hour}:00`;
    const r = getTradeR(trade);
    
    if (!rByTimeMap[timeKey]) {
      rByTimeMap[timeKey] = 0;
      countByTimeMap[timeKey] = 0;
    }
    
    rByTimeMap[timeKey] += r;
    countByTimeMap[timeKey]++;
  });
  
  const rByTimeDistribution = Object.keys(rByTimeMap)
    .sort((a, b) => {
      const hourA = parseInt(a.split(':')[0]);
      const hourB = parseInt(b.split(':')[0]);
      return hourA - hourB;
    })
    .map(timeKey => ({
      time: timeKey,
      r: rByTimeMap[timeKey],
      count: countByTimeMap[timeKey],
      avgR: rByTimeMap[timeKey] / countByTimeMap[timeKey]
    }));

  // Prepare duration vs R scatter chart data
  const durationVsR = realTrades
    .filter(trade => trade.duration)
    .map(trade => ({
      duration: parseInt(trade.duration),
      r: getTradeR(trade),
      outcome: trade.outcome,
      symbol: trade.symbol,
      userId: trade.uid,
      userName: trade.userDisplayName || trade.userName
    }));

  // Prepare monthly performance data
  const tradeDates = realTrades.map(trade => new Date(trade.date));
  const firstTradeDate = new Date(Math.min(...tradeDates));
  const lastTradeDate = new Date(Math.max(...tradeDates));
      
  const monthlyRMap = {};
  const monthlyTradeCountMap = {};
  const monthlyWinCountMap = {};
  
  realTrades.forEach(trade => {
    const date = new Date(trade.date);
    const monthKey = safeFormatDate(date, 'yyyy-MM');
    const r = getTradeR(trade);
    
    if (!monthlyRMap[monthKey]) {
      monthlyRMap[monthKey] = 0;
      monthlyTradeCountMap[monthKey] = 0;
      monthlyWinCountMap[monthKey] = 0;
    }
    
    monthlyRMap[monthKey] += r;
    monthlyTradeCountMap[monthKey]++;
    
    if (trade.outcome === 'Take Profit') {
      monthlyWinCountMap[monthKey]++;
    }
  });
  
  // Generate all months in range
  const allMonths = eachMonthOfInterval({
    start: startOfMonth(firstTradeDate),
    end: endOfMonth(lastTradeDate)
  });
  
  const monthlyPerformance = allMonths.map(date => {
    const monthKey = safeFormatDate(date, 'yyyy-MM');
    const monthName = safeFormatDate(date, 'MMM yyyy');
    const r = monthlyRMap[monthKey] || 0;
    const count = monthlyTradeCountMap[monthKey] || 0;
    const winCount = monthlyWinCountMap[monthKey] || 0;
    
    return {
      month: monthName,
      monthKey,
      r,
      count,
      avgR: count ? r / count : 0,
      winRate: count ? (winCount / count) * 100 : 0
    };
  });

  // Prepare success rate by timeframe data
  const successRateByTimeframeMap = {
    mainTimeframe: {},
    sessionChannelTimeframe: {},
    triggerIdentificationTimeframe: {},
    entryConfirmationTimeframe: {}
  };
  
  const countByTimeframeMap = {
    mainTimeframe: {},
    sessionChannelTimeframe: {},
    triggerIdentificationTimeframe: {},
    entryConfirmationTimeframe: {}
  };
  
  const winsByTimeframeMap = {
    mainTimeframe: {},
    sessionChannelTimeframe: {},
    triggerIdentificationTimeframe: {},
    entryConfirmationTimeframe: {}
  };
  
  realTrades.forEach(trade => {
    ['mainTimeframe', 'sessionChannelTimeframe', 'triggerIdentificationTimeframe', 'entryConfirmationTimeframe'].forEach(timeframeType => {
      const timeframe = trade[timeframeType];
      if (!timeframe) return;
      
      if (!countByTimeframeMap[timeframeType][timeframe]) {
        countByTimeframeMap[timeframeType][timeframe] = 0;
        winsByTimeframeMap[timeframeType][timeframe] = 0;
      }
      
      countByTimeframeMap[timeframeType][timeframe]++;
      
      if (trade.outcome === 'Take Profit') {
        winsByTimeframeMap[timeframeType][timeframe]++;
      }
    });
  });
  
  const successRateByTimeframe = [];
  
  ['mainTimeframe', 'sessionChannelTimeframe', 'triggerIdentificationTimeframe', 'entryConfirmationTimeframe'].forEach(timeframeType => {
    Object.keys(countByTimeframeMap[timeframeType]).forEach(timeframe => {
      const count = countByTimeframeMap[timeframeType][timeframe];
      const wins = winsByTimeframeMap[timeframeType][timeframe];
      
      successRateByTimeframe.push({
        timeframeType: timeframeType === 'mainTimeframe' ? 'טווח ראשי' :
                       timeframeType === 'sessionChannelTimeframe' ? 'תעלה סשן' :
                       timeframeType === 'triggerIdentificationTimeframe' ? 'זיהוי טריגר' :
                       'אישור כניסה',
        timeframe,
        count,
        successRate: count ? (wins / count) * 100 : 0
      });
    });
  });

  // Prepare TP distribution data
  const tpDistributionMap = {};
  
  realTrades.forEach(trade => {
    if (!trade.tpMilestones) return;
    
    const exitMilestone = trade.tpMilestones.find(m => m.status === 'יציאה');
    if (!exitMilestone) return;
    
    const tp = exitMilestone.tp;
    
    if (!tpDistributionMap[tp]) {
      tpDistributionMap[tp] = 0;
    }
    
    tpDistributionMap[tp]++;
  });
  
  const tpDistribution = Object.keys(tpDistributionMap)
    .map(tp => ({
      tp,
      count: tpDistributionMap[tp]
    }))
    .sort((a, b) => {
      if (a.tp === "-1R") return 1;
      if (b.tp === "-1R") return -1;
      
      const valueA = parseFloat(a.tp.split(':')[1] || 0);
      const valueB = parseFloat(b.tp.split(':')[1] || 0);
      
      return valueA - valueB;
    });

  // Prepare stop reasons data
  const stopReasonsMap = {};
  
  realTrades
    .filter(trade => trade.outcome === 'Stop Loss')
    .forEach(trade => {
      const reason = trade.stopReason || 'לא צוין';
      
      if (!stopReasonsMap[reason]) {
        stopReasonsMap[reason] = 0;
      }
      
      stopReasonsMap[reason]++;
    });
  
  const stopReasons = Object.keys(stopReasonsMap).map(reason => ({
    reason,
    count: stopReasonsMap[reason]
  }));

  // Prepare market phase performance data
  const marketPhaseMap = {
    marketSeason: {},
    weeklyWavePhase: {},
    dailyWavePhase: {}
  };
  
  const marketPhaseCountMap = {
    marketSeason: {},
    weeklyWavePhase: {},
    dailyWavePhase: {}
  };
  
  realTrades.forEach(trade => {
    ['marketSeason', 'weeklyWavePhase', 'dailyWavePhase'].forEach(phaseType => {
      const phase = trade[phaseType];
      if (!phase) return;
      
      const r = getTradeR(trade);
      
      if (!marketPhaseMap[phaseType][phase]) {
        marketPhaseMap[phaseType][phase] = 0;
        marketPhaseCountMap[phaseType][phase] = 0;
      }
      
      marketPhaseMap[phaseType][phase] += r;
      marketPhaseCountMap[phaseType][phase]++;
    });
  });
  
  const marketPhasePerformance = [];
  
  ['marketSeason', 'weeklyWavePhase', 'dailyWavePhase'].forEach(phaseType => {
    Object.keys(marketPhaseMap[phaseType]).forEach(phase => {
      const r = marketPhaseMap[phaseType][phase];
      const count = marketPhaseCountMap[phaseType][phase];
      
      marketPhasePerformance.push({
        phaseType: phaseType === 'marketSeason' ? 'עונת שוק' :
                  phaseType === 'weeklyWavePhase' ? 'פאזת וייקוף שבועי' :
                  'פאזת וייקוף יומי',
        phase,
        r,
        count,
        avgR: r / count
      });
    });
  });

  return {
    cumulativeR: computedCumulativeR,
    rByDay,
    winRateByDay,
    rBySymbol,
    rBySession,
    rByTrigger,
    rByReport,
    rByTimeDistribution,
    durationVsR,
    monthlyPerformance,
    successRateByTimeframe,
    tpDistribution,
    stopReasons,
    marketPhasePerformance,
    rByUser
  };
};

// Get available triggers from trades
export const getTriggers = (trades) => {
  const triggers = new Set();
  trades.forEach(trade => {
    if (trade.matchedTrigger) {
      triggers.add(trade.matchedTrigger);
    }
  });
  return ['all', ...Array.from(triggers)];
};